import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import brandLogo from '../assets/logos/logo_transparent_background.png';
import ChatModal from './common/ChatModal';
import materiaService from '../services/MateriaService';
import colegioApiService from '../services/ColegioApiService';
import AsistenteService from '../services/AsistenteService';
import './SubjectCardsGrid.css';

const SubjectCardsGrid = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [assistantsForSubject, setAssistantsForSubject] = useState([]);
  const [modalStep, setModalStep] = useState('consent'); // consent | select | empty
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [publicYears, setPublicYears] = useState([]);
  const [loadingMaterias, setLoadingMaterias] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatAssistant, setChatAssistant] = useState(null);
  const [chatPassword, setChatPassword] = useState('');
  const [chatTitle, setChatTitle] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isValidatingPassword, setIsValidatingPassword] = useState(false);
  const location = useLocation();
  const [colegioId, setColegioId] = useState('');
  const [colegioError, setColegioError] = useState('');
  const [assistantNickname, setAssistantNickname] = useState('');
  const [directAssistant, setDirectAssistant] = useState(null);
  const [directLoading, setDirectLoading] = useState(false);
  const [directError, setDirectError] = useState('');
  const [directColegioId, setDirectColegioId] = useState('');
  const [colegioNombreUrl, setColegioNombreUrl] = useState('');
  const [directPassword, setDirectPassword] = useState('');
  const [directPasswordError, setDirectPasswordError] = useState('');
  const [directValidating, setDirectValidating] = useState(false);
  const [isResolvingColegio, setIsResolvingColegio] = useState(false);

  const buildSlug = (value) => {
    if (!value) return '';
    return value.trim().toLowerCase().replace(/\s+/g, '_');
  };

  const getSubjectLabel = (materia) => {
    const nombre = materia?.nombre || materia?.Nombre || 'Materia';
    const division = String(materia?.division || materia?.Division || '').trim();
    return division ? `${nombre} · División ${division}` : nombre;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('colegioId') || params.get('idColegio') || '';
    const colegioParam = params.get('colegio') || '';
    const assistantParam = params.get('asistente') || params.get('assistant') || params.get('nickname') || '';

    setColegioError('');
    setDirectError('');
    setDirectAssistant(null);
    setDirectColegioId('');
    setDirectPassword('');
    setDirectPasswordError('');
    setAssistantNickname(assistantParam);
    setColegioNombreUrl(colegioParam);

    if (idParam) {
      setIsResolvingColegio(false);
      setColegioId(idParam);
      return;
    }

    if (!colegioParam) {
      setIsResolvingColegio(false);
      setColegioId('');
      return;
    }

    const nombreUrl = buildSlug(colegioParam);
    const resolveColegio = async () => {
      setIsResolvingColegio(true);
      try {
        const response = await colegioApiService.getColegioByNombreUrl(nombreUrl);
        const list = response?.colegios || response?.Colegios || response?.data || response || [];
        const colegio = Array.isArray(list) ? list[0] : list;
        const resolvedId = colegio?.id || colegio?.Id;
        if (!resolvedId) {
          throw new Error('No se encontró el colegio solicitado.');
        }
        setColegioId(resolvedId);
      } catch (error) {
        setColegioId('');
        setColegioError(error.message || 'No pudimos identificar el colegio.');
      } finally {
        setIsResolvingColegio(false);
      }
    };

    resolveColegio();
  }, [location.search]);

  useEffect(() => {
    const loadDirectAssistant = async () => {
      if (!assistantNickname) {
        setDirectAssistant(null);
        return;
      }

      if (!colegioNombreUrl) {
        setDirectError('Falta el colegio en la URL.');
        return;
      }

      setDirectLoading(true);
      setDirectError('');
      try {
        const response = await AsistenteService.getAsistentePublicoPorNickname(assistantNickname, colegioNombreUrl);
        const asistente = response?.asistente || response?.Asistente || response;
        if (!asistente) {
          throw new Error('No se encontró el asistente solicitado.');
        }

        const resolvedColegioId = asistente.idColegio || asistente.IdColegio || colegioId;
        if (resolvedColegioId) {
          setDirectColegioId(resolvedColegioId);
          if (resolvedColegioId !== colegioId) {
            setColegioId(resolvedColegioId);
          }
        }

        setDirectAssistant(asistente);
        const requierePassword = asistente.requierePassword ?? asistente.RequierePassword ?? false;
        if (!requierePassword) {
          const assistantName = asistente.nombre || asistente.Nombre || 'Asistente';
          setChatAssistant(asistente);
          setChatPassword('');
          setChatTitle(`Asistente ${assistantName}`);
          setShowChatModal(true);
        }
      } catch (error) {
        setDirectError(error.message || 'No pudimos cargar el asistente.');
      } finally {
        setDirectLoading(false);
      }
    };

    loadDirectAssistant();
  }, [assistantNickname, colegioNombreUrl, colegioId]);

  const handleDirectPasswordConfirm = async () => {
    if (!directAssistant) return;
    const assistantId = directAssistant.idOpenAIAssistant || directAssistant.IdOpenAIAssistant || directAssistant.id;
    if (!assistantId) {
      setDirectPasswordError('No se encontró el asistente seleccionado.');
      return;
    }
    if (!directPassword.trim()) {
      setDirectPasswordError('Ingresa el password para continuar.');
      return;
    }
    const colegioIdToUse = directColegioId || colegioId;
    if (!colegioIdToUse) {
      setDirectPasswordError('Falta el colegio para validar el password.');
      return;
    }

    setDirectValidating(true);
    setDirectPasswordError('');
    try {
      const result = await materiaService.validateAsistentePassword(assistantId, colegioIdToUse, directPassword.trim());
      const valido = result?.valido ?? result?.Valido;
      if (!valido) {
        setDirectPasswordError('El password ingresado no es correcto.');
        return;
      }
      const assistantName = directAssistant.nombre || directAssistant.Nombre || 'Asistente';
      setChatAssistant(directAssistant);
      setChatPassword(directPassword.trim());
      setChatTitle(`Asistente ${assistantName}`);
      setShowChatModal(true);
    } catch (error) {
      setDirectPasswordError(error.message || 'No pudimos validar el password.');
    } finally {
      setDirectValidating(false);
    }
  };



  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    const fetchMaterias = async () => {
      setLoadingMaterias(true);
      setMateriasError('');
      try {
        if (!colegioId) {
          setMateriasError(colegioError || '');
          setPublicYears([]);
          return;
        }
        const list = await materiaService.getMateriasPublicasPorAnioConAsistentes(colegioId);
        setPublicYears(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasError(error.message || 'No pudimos cargar las materias públicas.');
      } finally {
        setLoadingMaterias(false);
      }
    };
    fetchMaterias();
  }, [colegioId, colegioError]);

  const handleMateriaClick = async (materia) => {
    if (!materia) return;
    if (!colegioId) {
      setMateriasError(colegioError || '');
      return;
    }
    setSelectedSubject(getSubjectLabel(materia));
    setSelectedAssistant(null);
    setAssistantsForSubject([]);
    setModalStep('consent');
    setPasswordInput('');
    setPasswordError('');
    setLoadingAssistants(true);
    setShowModal(true);

    try {
      const asistentes = materia?.asistentes || materia?.Asistentes || [];
      const list = Array.isArray(asistentes) ? asistentes : [];
      setAssistantsForSubject(list);
      if (list.length === 0) {
        setModalStep('empty');
      } else if (list.length === 1) {
        setSelectedAssistant(list[0]);
        setModalStep('consent');
      } else {
        setModalStep('select');
      }
    } catch (error) {
      setAssistantsForSubject([]);
      setModalStep('empty');
    } finally {
      setLoadingAssistants(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedAssistant) {
      setModalStep('empty');
      return;
    }
    const requierePassword = selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword ?? false;
    if (requierePassword) {
      if (!passwordInput.trim()) {
        setPasswordError('Ingresa el password para continuar.');
        setShowModal(true);
        return;
      }
      setIsValidatingPassword(true);
      setPasswordError('');
      try {
        const assistantId = selectedAssistant.idOpenAIAssistant || selectedAssistant.IdOpenAIAssistant || selectedAssistant.id;
        if (!assistantId) {
          setPasswordError('No se encontró el asistente seleccionado.');
          setShowModal(true);
          return;
        }
        const result = await materiaService.validateAsistentePassword(assistantId, colegioId, passwordInput.trim());
        const valido = result?.valido ?? result?.Valido;
        if (!valido) {
          setPasswordError('El password ingresado no es correcto.');
          setShowModal(true);
          return;
        }
      } catch (error) {
        setPasswordError(error.message || 'No pudimos validar el password.');
        setShowModal(true);
        return;
      } finally {
        setIsValidatingPassword(false);
      }
    }
    const subjectTitle = selectedSubject;
    setShowModal(false);
    setSelectedSubject('');
    const requiresPassword = selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword ?? false;
    setChatAssistant(selectedAssistant);
    setChatPassword(requiresPassword ? passwordInput.trim() : '');
    setChatTitle(subjectTitle ? `Asistente ${subjectTitle}` : 'Asistente');
    setSelectedAssistant(null);
    setAssistantsForSubject([]);
    setModalStep('consent');
    setPasswordInput('');
    setPasswordError('');
    setShowChatModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedSubject('');
    setSelectedAssistant(null);
    setAssistantsForSubject([]);
    setModalStep('consent');
    setPasswordInput('');
    setPasswordError('');
  };

  const handleCloseChat = () => {
    setShowChatModal(false);
    setChatAssistant(null);
    setChatPassword('');
    setChatTitle('');
  };

  const colorPalette = ['primary', 'success', 'warning', 'info', 'danger', 'secondary', 'dark'];
  const yearCards = useMemo(() => {
    if (!Array.isArray(publicYears)) return [];
    return publicYears.map((year, index) => {
      const valor = year.valor ?? year.Valor;
      const name = valor ? `${valor}º Año` : 'Sin año';
      return {
        key: year.idAnio || year.IdAnio || name,
        name,
        color: colorPalette[index % colorPalette.length],
        subjects: year.materias || year.Materias || []
      };
    });
  }, [publicYears]);

  const directMode = Boolean(assistantNickname);
  if (directMode) {
    const requiresPassword = directAssistant?.requierePassword ?? directAssistant?.RequierePassword ?? false;
    const assistantName = directAssistant?.nombre || directAssistant?.Nombre || assistantNickname || 'Asistente';
    const showDirectModal = directLoading || directError;
    const directTitle = directLoading ? 'Preparando asistente' : 'No pudimos abrir el asistente';
    const directMessage = directLoading
      ? 'Estamos cargando la informacion necesaria para iniciar el chat.'
      : directError;

    return (
      <div className="subject-cards-container">
        {showDirectModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '2rem',
                maxWidth: '420px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>
                {directLoading ? '⏳' : '⚠️'}
              </div>
              <h3 style={{ marginTop: 0 }}>{directTitle}</h3>
              <p style={{ color: '#475569', marginBottom: '1.25rem' }}>{directMessage}</p>
              {directLoading ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem' }}>
                  <i className="fas fa-spinner fa-spin" />
                  <span style={{ fontWeight: 600 }}>Cargando...</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => window.location.assign('/')}
                  style={{
                    marginTop: '0.25rem',
                    padding: '0.7rem 1.4rem',
                    border: 'none',
                    borderRadius: '999px',
                    backgroundColor: '#0f172a',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Volver al inicio
                </button>
              )}
            </div>
          </div>
        )}

        {!directLoading && !directError && requiresPassword && !showChatModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9998
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '18px',
                padding: '2rem',
                maxWidth: '420px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
                textAlign: 'left'
              }}
            >
              <p style={{ marginTop: 0, marginBottom: '0.35rem', color: '#64748b', fontWeight: 600, letterSpacing: '0.04em' }}>
                Acceso privado
              </p>
              <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Asistente {assistantName}</h3>
              <p style={{ color: '#475569', marginBottom: '1.25rem' }}>
                Este asistente requiere password para iniciar el chat.
              </p>
              <div style={{ maxWidth: '320px' }}>
                <input
                  type="password"
                  value={directPassword}
                  onChange={(e) => setDirectPassword(e.target.value)}
                  placeholder="Password del asistente"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleDirectPasswordConfirm();
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}
                />
                {directPasswordError && (
                  <p style={{ marginTop: '0.5rem', color: '#b91c1c', fontWeight: 600 }}>
                    {directPasswordError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleDirectPasswordConfirm}
                  disabled={directValidating}
                  style={{
                    marginTop: '0.9rem',
                    width: '100%',
                    padding: '0.75rem',
                    border: 'none',
                    backgroundColor: '#0f172a',
                    color: 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                >
                  {directValidating ? 'Validando...' : 'Iniciar chat'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showChatModal && (
          <ChatModal
            isOpen={showChatModal}
            onClose={handleCloseChat}
            assistant={chatAssistant}
            publicChat
            colegioId={directColegioId || colegioId}
            password={chatPassword || null}
            headerTitle={chatTitle || `Asistente ${assistantName}`}
          />
        )}
      </div>
    );
  }

  return (
    <div className="subject-cards-container">
      {/* Header con logo */}
      <div className="header-section">
        <div className="container">
          <div className="row justify-content-center align-items-center">
            <div className="col-12 text-center">
              <div className="logo-container mb-4">
                <img 
                  src={brandLogo} 
                  alt="aluxIA" 
                  className="brand-logo"
                />
              </div>
              <h1 className="main-title">
                <span className="title-gradient">Departamento de Ing. En Sistemas de Información</span>
              </h1>
              <h2 className="subtitle">Plataforma de asistencia académica</h2>
              <div className="decorative-line mx-auto"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de cards */}
      <div className="container my-5">
        <div className="row g-4">
          {(loadingMaterias || isResolvingColegio) && (
            <div className="col-12">
              <div className="alert alert-info text-center">
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }} />
                Estamos obteniendo el listado de materias y asistentes...
              </div>
            </div>
          )}
          {!loadingMaterias && !isResolvingColegio && materiasError && (
            <div className="col-12">
              <div className="alert alert-danger text-center">{materiasError}</div>
            </div>
          )}
          {!loadingMaterias && !isResolvingColegio && !materiasError && yearCards.length === 0 && (
            <div className="col-12">
              <div className="alert alert-warning text-center">No hay materias públicas disponibles.</div>
            </div>
          )}
          {!loadingMaterias && !isResolvingColegio && !materiasError && yearCards.map((yearData) => (
            <div key={yearData.key} className="col-12 col-md-6 col-lg-4">
              <div className={`card subject-card border-${yearData.color} h-100`}>
                <div className={`card-header bg-${yearData.color} text-white`}>
                  <h5 className="card-title mb-0 d-flex align-items-center">
                    <span className="me-2 fs-4">🎓</span>
                    {yearData.name}
                  </h5>
                </div>
                <div className="card-body">
                  <button
                    className={`btn btn-outline-${yearData.color} w-100 mb-3`}
                    onClick={() => toggleSection(yearData.key)}
                    aria-expanded={expandedSections[yearData.key]}
                  >
                    {expandedSections[yearData.key] ? 'Ocultar Materias' : 'Ver Materias'}
                    <i className={`ms-2 fas fa-chevron-${expandedSections[yearData.key] ? 'up' : 'down'}`}></i>
                  </button>
                  
                  <div className={`collapse ${expandedSections[yearData.key] ? 'show' : ''}`}>
                    <div className="links-container">
                      {yearData.subjects.map((subject, index) => (
                        <div key={subject.id || subject.Id || index} className="subject-link-item mb-2">
                          <button
                            onClick={() => handleMateriaClick(subject)}
                            className={`btn btn-sm btn-outline-${yearData.color} w-100 text-start`}
                            type="button"
                          >
                            <i className="fas fa-robot me-2"></i>
                            {getSubjectLabel(subject)}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="footer-section mt-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <p className="footer-text">
                © 2025 aluxIA
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal de Advertencia */}
      {showModal && (
        <div 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
          onClick={handleCancel}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '15px',
              maxWidth: '600px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
              <h2 style={{ color: '#e17055', marginBottom: '1rem' }}>
                {modalStep === 'select' ? 'SELECCIONA UN ASISTENTE' : 'IMPORTANTE'}
              </h2>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              {loadingAssistants && (
                <p style={{ marginBottom: '1rem', fontWeight: '500', textAlign: 'center' }}>
                  Buscando asistentes disponibles...
                </p>
              )}

              {!loadingAssistants && modalStep === 'empty' && (
                <div>
                  <p style={{ marginBottom: '1rem', fontWeight: '500' }}>
                    No hay asistentes disponibles para la materia:
                  </p>
                  <p style={{ fontWeight: 'bold', color: '#007bff', fontSize: '1.2rem', marginBottom: '1rem' }}>
                    📚 {selectedSubject}
                  </p>
                </div>
              )}

              {!loadingAssistants && modalStep === 'select' && (
                <div>
                  <p style={{ marginBottom: '1rem', fontWeight: '500' }}>
                    Materia: <strong>{selectedSubject}</strong>
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {assistantsForSubject.map((assistant) => (
                      <button
                        key={assistant.idOpenAIAssistant || assistant.IdOpenAIAssistant || assistant.id}
                        type="button"
                        onClick={() => {
                          setSelectedAssistant(assistant);
                          setModalStep('consent');
                          setPasswordInput('');
                          setPasswordError('');
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#f8fafc',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🤖 {assistant.nombre || assistant.Nombre || 'Asistente'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!loadingAssistants && modalStep === 'consent' && (
                <div>
                  <p style={{ marginBottom: '1rem', fontWeight: '500' }}>
                    A continuación accederás al asistente de la materia:
                  </p>
                  <p style={{ fontWeight: 'bold', color: '#007bff', fontSize: '1.2rem', marginBottom: '1rem' }}>
                    📚 {selectedSubject}
                  </p>
                  {selectedAssistant && (
                    <p style={{ marginBottom: '1rem', fontWeight: '600' }}>
                      Asistente: <span style={{ color: '#1f2937' }}>{selectedAssistant.nombre || selectedAssistant.Nombre}</span>
                    </p>
                  )}
                  <div style={{ 
                    padding: '1rem', 
                    backgroundColor: '#fff3cd', 
                    borderRadius: '10px',
                    borderLeft: '4px solid #ffc107',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ margin: 0, fontWeight: 'bold', textAlign: 'center' }}>
                      Las respuestas generadas por este asistente tienen carácter orientativo y deben considerarse como apoyo al proceso de aprendizaje.
                    </p>
                    <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold', fontStyle: 'italic', textAlign: 'center' }}>
                      Se recomienda contrastar la información con las fuentes oficiales de la cátedra o con el equipo docente.
                    </p>
                  </div>
                  {selectedAssistant && (selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword) && (
                    <div style={{ marginTop: '1rem' }}>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Password de acceso
                      </label>
                      <input
                        type="password"
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        placeholder="Ingresa el password del asistente"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleConfirm();
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                      {passwordError && (
                        <p style={{ marginTop: '0.5rem', color: '#b91c1c', fontWeight: '600' }}>
                          {passwordError}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button 
                onClick={handleCancel}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #6c757d',
                  backgroundColor: 'transparent',
                  color: '#6c757d',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ❌ Cancelar
              </button>
              {modalStep === 'consent' && (
                <button 
                  onClick={handleConfirm}
                  disabled={isValidatingPassword}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    backgroundColor: '#ffc107',
                    color: 'white',
                    borderRadius: '25px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {isValidatingPassword ? 'Validando...' : '✅ Entendido, Continuar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Assistant */}
      {showChatModal && (
        <ChatModal
          isOpen={showChatModal}
          onClose={handleCloseChat}
          assistant={chatAssistant}
          publicChat
          colegioId={colegioId}
          password={chatPassword || null}
          headerTitle={chatTitle}
        />
      )}
    </div>
  );
};

export default SubjectCardsGrid;
